// Career Map — Stage 2 detail-page content layer (build/SSR only, no DB).
//
// A career gets a detail page IFF a content file exists at
// src/data/career_pages/<career_id>.json. The glob below is eager, so dropping
// a new JSON in that folder makes the card appear (chip turns clickable, route
// resolves, sitemap includes it) with zero wiring — the "staged rollout" the
// ТЗ asks for. Name / riasec / entry / sphere / similar come from the existing
// base data (see data.ts); the content file holds ONLY the prose.

import vectorsData from "../data/career_vectors_1822.json";
import {
  careerNameOf,
  clusterOf,
  entryOf,
  riasecOf,
  sphereMetaOf,
  sphereSlugOf,
  type Lang,
} from "./data";

type L = { az: string; ru: string; en: string };
type CareerPageFile = {
  lead: L;
  tasks: L[];
  skills: L[];
  path_steps: L[];
  environment: L;
  environment_tag: string;
};

// Eager glob → { "../data/career_pages/data_analyst.json": {default: {...}} }.
const FILES = import.meta.glob("../data/career_pages/*.json", { eager: true }) as Record<
  string,
  { default: CareerPageFile } | CareerPageFile
>;

const PAGES: Record<string, CareerPageFile> = {};
for (const [path, mod] of Object.entries(FILES)) {
  const id = path.slice(path.lastIndexOf("/") + 1).replace(/\.json$/, "");
  PAGES[id] = (mod as { default?: CareerPageFile }).default ?? (mod as CareerPageFile);
}

/** Set of career ids that currently have a detail page. */
export const CARDED_IDS: ReadonlySet<string> = new Set(Object.keys(PAGES));

/** Does this career have a published detail page? */
export function hasCareerPage(id: string): boolean {
  return CARDED_IDS.has(id);
}

// ── Similar careers (cosine over the 6-D O*NET interest vector) ──────────
type Vectors = { clusters: Record<string, Record<string, { vector?: Record<string, number> }>> };
const V = vectorsData as unknown as Vectors;
const AXES = ["R", "I", "A", "S", "E", "C"] as const;

function vectorOf(id: string): number[] | null {
  const cl = clusterOf(id);
  const v = cl ? V.clusters?.[cl]?.[id]?.vector : undefined;
  if (!v) return null;
  return AXES.map((a) => v[a] ?? 0);
}
function cosine(a: number[], b: number[]): number {
  let dot = 0,
    na = 0,
    nb = 0;
  for (let i = 0; i < a.length; i++) {
    dot += a[i] * b[i];
    na += a[i] * a[i];
    nb += b[i] * b[i];
  }
  return na && nb ? dot / (Math.sqrt(na) * Math.sqrt(nb)) : 0;
}

export type SimilarCareer = {
  id: string;
  name: string;
  sphereSlug: string;
  sphereName: string;
};

/**
 * Сколько соседей показываем. Шесть, а не четыре: страниц всего пятьдесят, и
 * плотная перелинковка между ними сейчас важнее разнообразия.
 */
export const SIMILAR_COUNT = 6;

/**
 * Потолок входящих ссылок на одну профессию.
 *
 * Без него «центральная» профессия попадала бы в соседи ко всем подряд:
 * замер по нынешним пятидесяти страницам дал максимум 11 при среднем 6, то
 * есть перекос маленький, но он есть и вырастет вместе с каталогом. Потолок
 * стоит на десяти и разводит ровно те лишние связи, а не перекраивает выбор.
 */
export const SIMILAR_MAX_INBOUND = 10;

/**
 * Соседи выбираются СРЕДИ СТРАНИЦ, КОТОРЫЕ СУЩЕСТВУЮТ, и считаются один раз
 * на весь сайт.
 *
 * ЧТО БЫЛО. Соседи брались по косинусу среди всех 290 профессий справочника,
 * а ссылкой становился только тот, у кого есть своя страница. Вероятность,
 * что четверо ближайших окажутся среди пятидесяти заведённых, мала — на живой
 * /careers/technology-it/data_analyst блок «похожие профессии» не давал НИ
 * ОДНОЙ ссылки: четыре плоские карточки без href. Блок, написанный ради
 * перелинковки, её не создавал.
 *
 * Правило «не вести туда, где страницы нет» при этом остаётся: чинится не
 * отрисовка, а множество, из которого выбираем.
 *
 * ПОЧЕМУ ЭТО ОБЩИЙ РАСЧЁТ, А НЕ ФУНКЦИЯ ОТ ОДНОГО id. Потолок входящих —
 * свойство всего графа: решить его, глядя на одну профессию, нельзя.
 * Назначение идёт по алфавиту, поэтому оно детерминировано: одна и та же
 * сборка даёт один и тот же граф, и три языка получают одинаковые ссылки.
 *
 * ЯЗЫК НЕ УЧАСТВУЕТ В ВЫБОРЕ — только в подписи. Иначе AZ, RU и EN разошлись
 * бы графами, и одна и та же страница вела бы в разные места.
 */
const NEIGHBOURS: Record<string, string[]> = (() => {
  const ids = [...CARDED_IDS].filter((id) => vectorOf(id) && sphereSlugOf(id)).sort();
  const inbound: Record<string, number> = {};
  const out: Record<string, string[]> = {};

  for (const id of ids) {
    const base = vectorOf(id)!;
    const own = sphereSlugOf(id);
    // Своя сфера вперёд, дальше по близости вектора. Профессия рядом по
    // смыслу полезнее человеку, чем чуть более близкая по цифрам из другой
    // отрасли; когда своих не хватает — добираем чужими, как и задумано.
    const ranked = ids
      .filter((o) => o !== id)
      .map((o) => ({
        id: o,
        sameSphere: sphereSlugOf(o) === own ? 1 : 0,
        score: cosine(base, vectorOf(o)!),
      }))
      .sort(
        (p, q) => q.sameSphere - p.sameSphere || q.score - p.score || p.id.localeCompare(q.id),
      );

    const picked: string[] = [];
    for (const r of ranked) {
      if ((inbound[r.id] ?? 0) >= SIMILAR_MAX_INBOUND) continue;
      picked.push(r.id);
      inbound[r.id] = (inbound[r.id] ?? 0) + 1;
      if (picked.length >= SIMILAR_COUNT) break;
    }
    out[id] = picked;
  }
  return out;
})();

/**
 * Соседи профессии — все со своей страницей, все ссылками.
 *
 * Возвращает пусто только для профессии без собственной страницы: у неё нет
 * и места, где этот список показать.
 */
export function similarCareers(id: string, lang: Lang): SimilarCareer[] {
  return (NEIGHBOURS[id] ?? []).map((otherId) => {
    const slug = sphereSlugOf(otherId)!;
    return {
      id: otherId,
      name: careerNameOf(otherId, lang)!,
      sphereSlug: slug,
      sphereName: sphereMetaOf(slug)!.name[lang],
    };
  });
}

/** Входящие ссылки по профессиям — для прибилдовой проверки перекоса. */
export function inboundCounts(): Record<string, number> {
  const n: Record<string, number> = {};
  for (const id of Object.keys(NEIGHBOURS)) n[id] ??= 0;
  for (const list of Object.values(NEIGHBOURS)) for (const o of list) n[o] = (n[o] ?? 0) + 1;
  return n;
}

/**
 * SEO <title> per locale (formulas review-pending). Длиннее 60 (16.10.2026) —
 * по очереди: без « | Yolutap», короткий вопрос с брендом, короткий без него.
 */
export function careerPageTitle(name: string, lang: Lang): string {
  const full: Record<Lang, string> = {
    az: `${name} kimdir və nə ilə məşğul olur?`,
    ru: `${name}: кто это и чем занимается?`,
    en: `${name}: who they are and what they do`,
  };
  const short: Record<Lang, string> = {
    az: `${name} kimdir?`,
    ru: `${name}: кто это?`,
    en: `${name}: who they are`,
  };
  const variants = [`${full[lang]} | Yolutap`, full[lang], `${short[lang]} | Yolutap`, short[lang]];
  return variants.find((t) => [...t].length <= 60) ?? variants[variants.length - 1];
}

/** BreadcrumbList + Occupation JSON-LD (no estimatedSalary — unverifiable). */
export function careerJsonLd(args: {
  name: string;
  description: string;
  sphereName: string;
  baseUrl: string;
  rootPath: string;
  spherePath: string;
  careerPath: string;
  breadcrumbRoot: string;
  /** Localized path_steps → Occupation.educationRequirements (Text). */
  pathSteps?: string[];
}): Record<string, unknown>[] {
  const abs = (p: string) => args.baseUrl + p;
  const occupation: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Occupation",
    name: args.name,
    description: args.description,
    occupationalCategory: args.sphereName,
  };
  // The career's route into the field, as a single Text value. path_steps are
  // already localized, so this stays in the page's language. Omitted entirely
  // if there are no steps (never emit an empty educationRequirements).
  const steps = (args.pathSteps ?? []).map((s) => s.trim()).filter(Boolean);
  if (steps.length) occupation.educationRequirements = steps.join("; ");
  return [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: args.breadcrumbRoot, item: abs(args.rootPath) },
        { "@type": "ListItem", position: 2, name: args.sphereName, item: abs(args.spherePath) },
        { "@type": "ListItem", position: 3, name: args.name, item: abs(args.careerPath) },
      ],
    },
    occupation,
  ];
}

/** BreadcrumbList JSON-LD for a sphere page (Map → Sphere). */
export function sphereJsonLd(args: {
  sphereName: string;
  baseUrl: string;
  rootPath: string;
  spherePath: string;
  breadcrumbRoot: string;
}): Record<string, unknown>[] {
  const abs = (p: string) => args.baseUrl + p;
  return [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: args.breadcrumbRoot, item: abs(args.rootPath) },
        { "@type": "ListItem", position: 2, name: args.sphereName, item: abs(args.spherePath) },
      ],
    },
  ];
}

export type CareerPageData = {
  id: string;
  name: string;
  riasec: string;
  entry: string;
  sphereSlug: string;
  sphereName: string;
  sphereIcon: string;
  lead: string;
  tasks: string[];
  skills: string[];
  pathSteps: string[];
  environment: string;
  environmentTag: string;
  similar: SimilarCareer[];
};

/**
 * Builds a detail page, or null if the career has no content file / is unknown.
 * The route should 404 on null.
 */
export function buildCareerPage(id: string, lang: Lang): CareerPageData | null {
  const page = PAGES[id];
  const slug = sphereSlugOf(id);
  const name = careerNameOf(id, lang);
  const meta = slug ? sphereMetaOf(slug) : undefined;
  if (!page || !slug || !name || !meta) return null;
  return {
    id,
    name,
    riasec: riasecOf(id),
    entry: entryOf(id),
    sphereSlug: slug,
    sphereName: meta.name[lang],
    sphereIcon: meta.icon,
    lead: page.lead[lang],
    tasks: page.tasks.map((t) => t[lang]),
    skills: page.skills.map((s) => s[lang]),
    pathSteps: page.path_steps.map((p) => p[lang]),
    environment: page.environment[lang],
    environmentTag: page.environment_tag,
    similar: similarCareers(id, lang),
  };
}
